import {
  Button,
  Box,
  Stack,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useOAuthProviders } from "../../../hooks/useOAuthProviders";
import { getCampaignAttribution } from "../../../utils/campaignAttribution";
import { logger } from "../../../utils/logger";
import { teacherInvitationPath } from "../../students/utils/invitationReturn";

import type { OAuthProvider } from "../../../hooks/useOAuthProviders";

interface SocialAuthButtonsProps {
  mode?: "login" | "register" | "link";
  onSuccess?: () => void;
  onError?: (error: string) => void;
  showDivider?: boolean;
  disabled?: boolean;
  compact?: boolean; // New: show as compact icon buttons
}

// SVG Icons for social providers
const GoogleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const MicrosoftIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 23 23">
    <path fill="#f3f3f3" d="M0 0h23v23H0z" />
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

const AppleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const FacebookIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const SocialAuthButtons: React.FC<SocialAuthButtonsProps> = ({
  mode = "login",
  onSuccess,
  onError,
  showDivider = true,
  disabled = false,
  compact = true, // Default to compact modern style
}) => {
  const { t } = useTranslation("auth");
  const location = useLocation();
  const [loading, setLoading] = useState<OAuthProvider | null>(null);

  // Use the new hook to check provider availability
  const { isProviderEnabled, isProviderConfigured } = useOAuthProviders();

  const handleOAuthClick = (provider: OAuthProvider): void => {
    if (loading || disabled) return;

    setLoading(provider);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3100";
      const clientUrl =
        import.meta.env.VITE_CLIENT_URL || window.location.origin;
      const currentParams = new URLSearchParams(window.location.search);
      const nativeLanguage =
        currentParams.get("native") ?? currentParams.get("lng") ?? "en";
      const targetLanguage = currentParams.get("target") ?? "es";
      const state = JSON.stringify({
        returnUrl: clientUrl,
        nativeLanguage,
        targetLanguage,
        attribution: getCampaignAttribution(),
        returnPath: teacherInvitationPath(
          (location.state as { from?: { pathname?: string } } | null)?.from
            ?.pathname,
        ),
      });

      // Build OAuth authorization URL
      let authUrl = "";

      switch (provider) {
        case "google":
          authUrl =
            `https://accounts.google.com/o/oauth2/v2/auth?` +
            new URLSearchParams({
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || "",
              redirect_uri: `${apiUrl}/api/auth/oauth/google/callback`,
              response_type: "code",
              scope: "openid profile email",
              prompt: "select_account", // Allow user to select different Google account
              access_type: "online",
              state,
            });
          break;

        case "microsoft":
          authUrl =
            `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
            new URLSearchParams({
              client_id: import.meta.env.VITE_MICROSOFT_CLIENT_ID || "",
              redirect_uri: `${apiUrl}/api/auth/oauth/microsoft/callback`,
              response_type: "code",
              scope: "openid profile email",
              state,
            });
          break;

        case "apple":
          authUrl =
            `https://appleid.apple.com/auth/authorize?` +
            new URLSearchParams({
              client_id: import.meta.env.VITE_APPLE_CLIENT_ID || "",
              redirect_uri: `${apiUrl}/api/auth/oauth/apple/callback`,
              response_type: "code id_token",
              response_mode: "form_post",
              scope: "name email",
              state,
            });
          break;

        case "facebook":
          authUrl =
            `https://www.facebook.com/v18.0/dialog/oauth?` +
            new URLSearchParams({
              client_id: import.meta.env.VITE_FACEBOOK_APP_ID || "",
              redirect_uri: `${apiUrl}/api/auth/oauth/facebook/callback`,
              scope: "email,public_profile",
              state,
            });
          break;
      }

      if (!authUrl) {
        onError?.(t("auth:errors.providerNotConfigured", { provider }));
        setLoading(null);
        return;
      }

      // Open OAuth in popup or redirect
      if (mode === "link") {
        // For linking, use popup to avoid navigation
        const popup = window.open(
          authUrl,
          `oauth-${provider}`,
          "width=600,height=700,scrollbars=yes",
        );

        // Poll for popup close
        const pollTimer = window.setInterval(() => {
          if (popup?.closed) {
            window.clearInterval(pollTimer);
            setLoading(null);
            onSuccess?.();
          }
        }, 500);
      } else {
        // For login/register, redirect to OAuth provider
        window.location.href = authUrl;
      }
    } catch (error) {
      logger.error(`${provider} OAuth error:`, error);
      onError?.(
        error instanceof Error
          ? error.message
          : t("auth:errors.authenticationFailed"),
      );
      setLoading(null);
    }
  };

  const renderButton = (
    provider: OAuthProvider,
    icon: React.ReactElement,
    label: string,
  ): React.ReactElement | null => {
    const providerEnabled = isProviderEnabled(provider);
    const providerConfigured = isProviderConfigured(provider);
    const isAvailable = providerEnabled && providerConfigured;
    const isLoading = loading === provider;
    const isDisabled =
      disabled || !isAvailable || (loading !== null && !isLoading);

    // Don't render if not configured at all
    if (!providerConfigured) {
      return null;
    }

    if (compact) {
      // Compact icon button style (modern)
      return (
        <Tooltip
          key={provider}
          title={
            !providerEnabled
              ? t("oauth.providerNotEnabled", {
                  provider: label,
                  defaultValue: `${label} (Not Enabled)`,
                })
              : label
          }
          arrow
        >
          <span>
            <IconButton
              onClick={() => handleOAuthClick(provider)}
              disabled={isDisabled}
              sx={{
                width: 56,
                height: 56,
                border: "2px solid",
                borderColor: isAvailable ? "divider" : "action.disabled",
                bgcolor: "background.paper",
                opacity: isAvailable ? 1 : 0.4,
                "&:hover": {
                  borderColor: isAvailable ? "primary.main" : "action.disabled",
                  bgcolor: isAvailable ? "action.hover" : "background.paper",
                },
                "&:disabled": {
                  opacity: 0.3,
                  cursor: "not-allowed",
                },
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : icon}
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    // Legacy full-width button style
    return (
      <Button
        key={provider}
        fullWidth
        variant="outlined"
        size="large"
        onClick={() => handleOAuthClick(provider)}
        disabled={isDisabled}
        sx={{
          justifyContent: "flex-start",
          textTransform: "none",
          borderColor: "rgba(0, 0, 0, 0.23)",
          color: "text.primary",
          py: 1.5,
          opacity: isAvailable ? 1 : 0.5,
          "&:hover": {
            borderColor: isAvailable
              ? "rgba(0, 0, 0, 0.4)"
              : "rgba(0, 0, 0, 0.23)",
            bgcolor: isAvailable ? "rgba(0, 0, 0, 0.04)" : "transparent",
          },
          "&:disabled": {
            opacity: isLoading ? 1 : 0.3,
          },
        }}
      >
        {isLoading ? (
          <CircularProgress size={20} sx={{ mr: 2 }} />
        ) : (
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 2,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, textAlign: "left" }}>
          {isLoading
            ? t("oauth.processing")
            : !providerEnabled
              ? t("oauth.providerNotEnabled", {
                  provider: label,
                  defaultValue: `${label} (Not Enabled)`,
                })
              : label}
        </Box>
      </Button>
    );
  };

  return (
    <Box>
      {compact ? (
        /* Compact mode: Icons in a row */
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mb: showDivider ? 0 : 2,
          }}
        >
          {renderButton("google", <GoogleIcon />, t("social.google"))}
          {renderButton("microsoft", <MicrosoftIcon />, t("social.microsoft"))}
          {renderButton("apple", <AppleIcon />, t("social.apple"))}
          {renderButton("facebook", <FacebookIcon />, t("social.facebook"))}
        </Box>
      ) : (
        /* Legacy mode: Full-width buttons stacked */
        <Stack spacing={2}>
          {renderButton("google", <GoogleIcon />, t("social.google"))}
          {renderButton("microsoft", <MicrosoftIcon />, t("social.microsoft"))}
          {renderButton("apple", <AppleIcon />, t("social.apple"))}
          {renderButton("facebook", <FacebookIcon />, t("social.facebook"))}
        </Stack>
      )}

      {showDivider && (
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t("social.or")}
          </Typography>
        </Divider>
      )}
    </Box>
  );
};

export default SocialAuthButtons;
